// MST
import { types, flow, cast } from 'mobx-state-tree'

// Interfaces
import { IDocumentProofDropdown, IDocumentProofDetail } from 'interfaces/back-office/master/document-proof'
import { 
  IDocumentProofSaveAttrs,
  IDocumentProofGetAllAttrs,
  IDocumentProofGetDropdownAttrs,
  IDocumentProofGetAttrs,
 } from 'interfaces/back-office/master/document-proof/document-proof-view.interface'
import { 
  IDocumentProofApiPaginatedResponse,
  IDocumentProofApiGetAllResponse,
  IDocumentProofApiGetDropdownResponse,
  IDocumentProofApiGetResponse,
 } from 'interfaces/back-office/master/document-proof/document-proof-api.interface'

// Extensions
import { withHelper_baseResponseDropdownTypesModel, withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'
import { withEnvironment } from 'models/extensions/with-environment'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Lodash
import cloneDeep from 'lodash/cloneDeep'
import omit from 'lodash/omit'

// Document Proof Props
const documentProofProps = {
  id: types.maybeNull(types.number),
  number: types.maybeNull(types.number),
  name: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
  poinKum: types.maybeNull(types.string)
}

// Document Proof Detail Model
const DocumentProofDetail = types.model('DocumentProofDetail').props(omit(documentProofProps, ['number']))

// Document Proof Model
const DocumentProofs = types
  .model('DocumentProofs')
  .props(withHelper_baseResponsePaginationTypes(types.model('DocumentProofList').props(documentProofProps)))

// Base Model
const DocumentProofStoreModal = types
  .model('DocumentProof')
  .props({
    masterDocumentProof_isLoading: types.optional(types.boolean, false),
    masterDocumentProof_dropdown: withHelper_baseResponseDropdownTypesModel('DocumentProofDropdown'),
    masterDocumentProof_isSaveLoading: types.optional(types.boolean, false),
    masterDocumentProof_documentProofs: types.optional(DocumentProofs, {}),
    masterDocumentProof_detail: types.optional(DocumentProofDetail, {}),
  })
  .extend(withEnvironment)
  .actions(self => ({
    masterDocumentProof_SET_LOADING: (payload: boolean): void => {
      self.masterDocumentProof_isLoading = payload
    },
    masterDocumentProof_SET_DOCUMENT_PROOF_DROPDOWN: (payload: IDocumentProofDropdown[]): void => {
      self.masterDocumentProof_dropdown = cast(payload.filter(dropdown => dropdown.value))
    },
    masterDocumentProof_CLEAR_DOCUMENT_PROOF_DROPDOWN: (): void => {
      self.masterDocumentProof_dropdown = cast([])
    },
    masterDocumentProof_SET_SAVE_LOADING: (payload: boolean): void => {
      self.masterDocumentProof_isSaveLoading = payload
    },
    masterDocumentProof_SET_DOCUMENT_PROOFS: (payload: IDocumentProofApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }
      self.masterDocumentProof_documentProofs = cast(mapPayload)
    },
    masterDocumentProof_SET_DOCUMENT_PROOF: (payload: IDocumentProofDetail): void => {
      self.masterDocumentProof_detail = cast(cloneDeep(payload))
    },
    masterDocumentProof_CLEAR_DOCUMENT_PROOFS: (): void => {
      self.masterDocumentProof_documentProofs = cast({})
    },
    masterDocumentProof_CLEAR_DOCUMENT_PROOF: (): void => {
      self.masterDocumentProof_detail = cast({})
    },
  }))
  .actions(self => ({
    /**
     * @description Fetch Document Proofs
     *
     * @param {IDocumentProofGetAllAttrs} payload
     *
     * @return {any} any
     */
    masterDocumentProof_fetchDocumentProofs: flow(function* (payload: IDocumentProofGetAllAttrs) {
      self.masterDocumentProof_SET_LOADING(true)

      const response: IDocumentProofApiGetAllResponse =
        yield self.environment.masterDocumentProofApi.documentProof_getDocumentProofs(payload)
      if (response.ok) self.masterDocumentProof_SET_DOCUMENT_PROOFS(response.res.data)
      self.masterDocumentProof_SET_LOADING(false)
    }),

    /**
     * @description Fetch Document Proof
     *
     * @param {IDocumentProofGetAttrs} payload
     *
     * @return {any} any
     */
    masterDocumentProof_fetchDocumentProof: flow(function* (payload: IDocumentProofGetAttrs) {
      self.masterDocumentProof_SET_LOADING(true)

      const response: IDocumentProofApiGetResponse = yield self.environment.masterDocumentProofApi.documentProof_getDocumentProof(
        {
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        },
      )
      if (response.ok) self.masterDocumentProof_SET_DOCUMENT_PROOF(response.res.data)

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.masterDocumentProof_SET_LOADING(false)
    }),

    /**
     * @description Save Document Proof
     *
     * @param {IDocumentProofSaveAttrs} payload
     *
     * @return {any} any
     */
    masterDocumentProof_saveDocumentProof: flow(function* (payload: IDocumentProofSaveAttrs) {
      self.masterDocumentProof_SET_SAVE_LOADING(true)

      const response: IDocumentProofApiGetAllResponse =
        yield self.environment.masterDocumentProofApi.masterDocumentProof_saveDocumentProof({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      // Do logic when request is success
      if (response.ok && payload?.onSuccess) payload.onSuccess()

      self.masterDocumentProof_SET_SAVE_LOADING(false)
    }),

    /**
     * @description Fetch Document Proof dropdown
     *
     * @param {IDocumentProofGetDropdownAttrs} payload
     *
     * @return {any} any
     */
    masterDocumentProof_fetchDocumentProofDropdown: flow(function* (payload: IDocumentProofGetDropdownAttrs) {
      self.masterDocumentProof_SET_LOADING(true)

      const response: IDocumentProofApiGetDropdownResponse =
        yield self.environment.masterDocumentProofApi.documentProof_getDocumentProofDropdown({
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        self.masterDocumentProof_SET_DOCUMENT_PROOF_DROPDOWN(response.res.data || [])

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.masterDocumentProof_SET_LOADING(false)
    }),
  }))

export const createDocumentProofStoreDefaultModel = () => types.optional(DocumentProofStoreModal, {})
